<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'InvoiceLinesView',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    companyId: {
      type: String,
      required: false,
    },
    partnerId: {
      type: String,
      required: false,
    },
    rentalId: {
      type: String,
      required: false,
    },
    contractId: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const getQueryParameters = (...args) => {
    return {
      company_id: props.companyId,
      partner_id: props.partnerId,
      rental_id: props.rentalId,
      contract_id: props.contractId,
    };
  };

  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
  }));
  const b_7315092382398562_columns = computed(() => [
    {
      field: 'invoice_kind',
      name: $fn.value.pgettext('Invoice', 'Invoice kind'),
    },
    {
      field: 'invoice_type',
      name: $fn.value.pgettext('Invoice', 'Invoice type'),
    },
    {
      field: 'invoice_number',
      name: $fn.value.pgettext('Invoice', 'Invoice number'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'invoice',
        params: {
          id: 'invoice_id',
        },
      },
    },
    {
      field: 'invoice_date',
      name: $fn.value.pgettext('Invoice', 'Invoice date'),
      type: 'date',
    },
    {
      field: 'fulfillment_date',
      name: $fn.value.pgettext('Invoice', 'Fulfillment date'),
      type: 'date',
    },
    {
      field: 'description',
      name: $fn.value.pgettext('InvoiceLine', 'Description'),
    },
    {
      field: 'quantity',
      name: $fn.value.pgettext('InvoiceLine', 'Quantity'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'unit',
      name: $fn.value.pgettext('InvoiceLine', 'Unit'),
    },
    {
      field: 'unit_price',
      name: $fn.value.pgettext('InvoiceLine', 'Price'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'discount',
      name: $fn.value.pgettext('InvoiceLine', 'Discount'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'net_amount',
      name: $fn.value.pgettext('InvoiceLine', 'Net'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'vat_amount',
      name: $fn.value.pgettext('InvoiceLine', 'VAT'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'amount',
      name: $fn.value.pgettext('InvoiceLine', 'Amount'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'tax_code',
      name: $fn.value.pgettext('InvoiceLine', 'Tax Code'),
    },
    {
      field: 'currency',
      name: $fn.value.pgettext('Invoice', 'Currency'),
    },
    {
      field: 'billing_period_start',
      name: $fn.value.pgettext('Invoice', 'Period start'),
      type: 'date',
    },
    {
      field: 'billing_period_end',
      name: $fn.value.pgettext('Invoice', 'Period end'),
      type: 'date',
    },
  ]);

  const $data = computed(() => ({
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_columnDefaults: b_7315092382398562_columnDefaults.value,
    b_7315092382398562_columns: b_7315092382398562_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('browse_invoice_lines', {
      parameters: getQueryParameters,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_columnDefaults', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_columns', nodeId: '7315092382398562' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      ds,
      ux,
      b_7315092382398562_columnDefaults,
      b_7315092382398562_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.companyId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('companyId');
    }
  );
  watch(
    () => props.partnerId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('partnerId');
    }
  );
  watch(
    () => props.rentalId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rentalId');
    }
  );
  watch(
    () => props.contractId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contractId');
    }
  );

  const act_7315092382398562_setData_4zt5rijsi_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };

  const act_7315092382398562_setData_4zt5rijsi = new actionTypes.SetDataAction({
    actionArgs: act_7315092382398562_setData_4zt5rijsi_getActionArgs,
  });
  const inst_evh_7315092382398562_selectedRowsChanged_xteilhxrs =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_setData_4zt5rijsi,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_7315092382398562_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7315092382398562_selectedRowsChanged_xteilhxrs],
      event: 'selectedRowsChanged',
    });

  const actions = {
    inst_evh_7315092382398562_selectedRowsChanged_xteilhxrs,
    inst_evh_7315092382398562_selectedRowsChanged,
  };

  const evh_7315092382398562_selectedRowsChanged_xteilhxrs = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7315092382398562_selectedRowsChanged_xteilhxrs.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <VueSlickgrid
    name="companyInvoices"
    :headerTitle="pgettext('InvoiceLines', 'Invoices')"
    :items="ds"
    checkbox-row-selection
    footer-totals
    export-to-csv
    :exportToCsvFilename="pgettext('filename', 'company-invoices')"
    :columnDefaults="b_7315092382398562_columnDefaults"
    :columns="b_7315092382398562_columns"
    ref="grid"
    @selectedRowsChanged="evh_7315092382398562_selectedRowsChanged($event, {})"
  >
  </VueSlickgrid>
</template>
